import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from '../services/axios';
import Modal from '../components/Modal';
import { compareApiDatesDesc, formatApiDate } from '../utils/date';

function PagarBoleta() {
  const { boletaId } = useParams();
  const [searchParams] = useSearchParams();
  const { user, API_URL, logout } = useAuth();
  const navigate = useNavigate();
  const [boleta, setBoleta] = useState(null);
  const [lecturas, setLecturas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [comprobante, setComprobante] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [modal, setModal] = useState({ isOpen: false, type: 'info', title: '', message: '' });
  const mpHabilitado = process.env.REACT_APP_MP_ENABLED === 'true';
  const [metodoPago, setMetodoPago] = useState('transferencia'); // 'transferencia' | 'mercadopago'
  const [metodosPermitidos, setMetodosPermitidos] = useState({
    transferencia: true,
    mercadoPago: true,
    politica: null,
  });
  const [mpLoading, setMpLoading] = useState(false);
  const [mpPagado, setMpPagado] = useState(false);
  // Estado de retorno de MP (back_urls)
  const mpPaymentStatus = searchParams.get('payment_status');

  useEffect(() => {
    cargarDatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boletaId]);

  useEffect(() => {
    if (!mpPaymentStatus) return;
    cargarDatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mpPaymentStatus]);

  const cargarDatos = async () => {
    try {
      // Cargar boleta completa (incluye desglose_consumo guardado)
      const boletaRes = await axios.get(`${API_URL}/boletas/${boletaId}`);
      setBoleta(boletaRes.data);

      try {
        const metodosRes = await axios.get(`${API_URL}/pagos/boleta/${boletaId}/metodos-permitidos`);
        const metodos = {
          transferencia: metodosRes.data.transferencia !== false,
          mercadoPago: metodosRes.data.mercadoPago !== false,
          politica: metodosRes.data.politica || null,
          motivo: metodosRes.data.motivo,
        };
        setMetodosPermitidos(metodos);

        if (!metodos.transferencia && metodos.mercadoPago && mpHabilitado) {
          setMetodoPago('mercadopago');
        } else if (!metodos.mercadoPago && metodos.transferencia) {
          setMetodoPago('transferencia');
        }
      } catch (err) {
        console.log('Error cargando métodos permitidos:', err);
      }

      // Cargar lecturas del historial si tiene medidor
      if (user.medidores && user.medidores.length > 0) {
        const medidorActivo = user.medidores.find(m => m.activo);
        if (medidorActivo) {
          try {
            const lecturasRes = await axios.get(`${API_URL}/lecturas?medidorId=${medidorActivo.id}`);
            const lecturasOrdenadas = lecturasRes.data
              .sort((a, b) => compareApiDatesDesc(a.fechaLectura, b.fechaLectura))
              .slice(0, 6)
              .reverse();
            setLecturas(lecturasOrdenadas);
          } catch (err) {
            console.log('Error cargando lecturas:', err);
          }
        }
      }
    } catch (error) {
      console.error('Error al cargar datos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopiarAlias = () => {
    navigator.clipboard.writeText('LIMA.RACIMO.PASADO');
    // Mostrar feedback visual temporal
    const btn = document.getElementById('copy-btn');
    btn.textContent = '✓ Copiado';
    setTimeout(() => {
      btn.innerHTML = '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>';
    }, 2000);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar tamaño (5MB máximo)
      if (file.size > 5 * 1024 * 1024) {
        setModal({
          isOpen: true,
          type: 'warning',
          title: 'Archivo demasiado grande',
          message: 'El archivo es demasiado grande. Máximo 5MB.',
        });
        return;
      }
      // Validar tipo
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];
      if (!validTypes.includes(file.type)) {
        setModal({
          isOpen: true,
          type: 'warning',
          title: 'Archivo no válido',
          message: 'Tipo de archivo no válido. Solo PDF, PNG o JPG.',
        });
        return;
      }
      setComprobante(file);
    }
  };

  const handleConfirmarPago = async () => {
    if (!metodosPermitidos.transferencia) {
      setModal({
        isOpen: true,
        type: 'warning',
        title: 'Método no habilitado',
        message: 'Este cliente no tiene habilitado este método de pago.',
      });
      return;
    }

    if (!comprobante) {
      setModal({
        isOpen: true,
        type: 'warning',
        title: 'Comprobante requerido',
        message: 'Por favor, adjunta el comprobante de pago.',
      });
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('comprobante', comprobante);
      formData.append('monto', boleta.total);
      formData.append('boletaId', boleta.id);

      await axios.post(`${API_URL}/pagos`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setShowSuccess(true);
    } catch (error) {
      console.error('Error al confirmar pago:', error);
      setModal({
        isOpen: true,
        type: 'error',
        title: 'Error al procesar el pago',
        message: 'No se pudo registrar el pago. Inténtalo de nuevo.',
      });
    } finally {
      setUploading(false);
    }
  };

  const formatearFecha = (fecha) => {
    return formatApiDate(fecha, {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatearFechaHora = (fecha) => {
    return new Date(fecha).toLocaleString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatearMonto = (monto) => {
    return parseFloat(monto || 0).toFixed(2);
  };

  const formatearPeriodo = (mes, anio) => {
    const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                   'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    return `${meses[mes - 1]} ${anio}`;
  };

  const calcularAltura = (consumo) => {
    if (!lecturas.length) return 0;
    const maxConsumo = Math.max(...lecturas.map(l => l.consumoM3 || 0));
    if (maxConsumo === 0) return 0;
    return ((consumo || 0) / maxConsumo) * 100;
  };

  const ultimoPagoRechazado = useMemo(() => {
    if (!boleta?.pagos || !boleta.pagos.length) return null;
    return [...boleta.pagos]
      .filter(p => p.estado === 'rechazado')
      .sort((a, b) => new Date(b.fechaPago) - new Date(a.fechaPago))[0] || null;
  }, [boleta]);

  const handlePagarConMP = async () => {
    if (!mpHabilitado || !metodosPermitidos.mercadoPago) {
      setModal({
        isOpen: true,
        type: 'warning',
        title: 'Método no habilitado',
        message: 'Este cliente no tiene habilitado este método de pago.',
      });
      return;
    }

    setMpLoading(true);
    try {
      const res = await axios.post(`${API_URL}/pagos/mercado-pago/boletas/${boleta.id}/preferencia`);
      window.location.href = res.data.initPoint;
    } catch (error) {
      setModal({
        isOpen: true,
        type: 'error',
        title: 'Error al iniciar el pago',
        message: error.response?.data?.message || 'No se pudo conectar con Mercado Pago. Inténtalo de nuevo.',
      });
    } finally {
      setMpLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const cerrarModal = () => {
    setModal((prev) => ({ ...prev, isOpen: false }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f6f7f8] dark:bg-[#101c22] flex items-center justify-center">
        <div className="text-lg text-gray-600 dark:text-gray-400">Cargando...</div>
      </div>
    );
  }

  if (!boleta) {
    return (
      <div className="min-h-screen bg-[#f6f7f8] dark:bg-[#101c22] flex items-center justify-center">
        <div className="text-lg text-gray-600 dark:text-gray-400">Boleta no encontrada</div>
      </div>
    );
  }

  const esPlanPago = boleta.estado === 'plan_pago';
  const transferenciaPermitida = metodosPermitidos.transferencia !== false;
  const mercadoPagoPermitido = mpHabilitado && metodosPermitidos.mercadoPago !== false;
  const sinMetodosPermitidos = !transferenciaPermitida && !mercadoPagoPermitido;

  // Modal de éxito
  if (showSuccess) {
    return (
      <div className="min-h-screen bg-[#f6f7f8] dark:bg-[#101c22] flex items-center justify-center p-4">
        <div className="bg-white dark:bg-[#1A2A33] rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-[#111618] dark:text-white mb-2">
            ¡Pago Registrado!
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            Tu comprobante ha sido enviado correctamente. Será revisado en las próximas 24-48 horas.
          </p>
          <button
            onClick={() => navigate('/cliente/dashboard')}
            className="w-full bg-[#007BFF] text-white font-semibold py-3 rounded-lg hover:bg-[#007BFF]/90 transition-colors"
          >
            Volver al Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f6f7f8] dark:bg-[#101c22]">
      {/* Header */}
      <header className="bg-white dark:bg-[#101c22] border-b border-gray-100 dark:border-gray-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-[#007BFF] flex items-center justify-center text-white font-bold text-base shadow-sm flex-shrink-0">
                {user?.nombre?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <div>
                <p className="text-xs text-[#617c89] dark:text-gray-400 leading-none mb-0.5">Portal Cliente</p>
                <p className="text-sm font-semibold text-[#111618] dark:text-white leading-none">
                  {user?.nombre} <span className="text-[#617c89] dark:text-gray-400 font-normal">· Padrón {user?.padron}</span>
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => navigate('/cliente/dashboard')}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-[#007BFF] hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors font-medium"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Volver
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-[#617c89] dark:text-gray-400 hover:text-[#111618] dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Salir
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Banner pago MP confirmado */}
          {mpPagado && (
            <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800 dark:border-green-800 dark:bg-green-900/20 dark:text-green-200">
              <p className="font-semibold">¡Pago acreditado!</p>
              <p className="mt-1">Tu pago fue procesado exitosamente por Mercado Pago. La factura se emitirá en breve.</p>
            </div>
          )}

          {/* Banner retorno Mercado Pago */}
          {mpPaymentStatus === 'approved' && (
            <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800 dark:border-green-800 dark:bg-green-900/20 dark:text-green-200">
              <p className="font-semibold">Pago acreditado</p>
              <p className="mt-1">Tu pago fue procesado exitosamente. La factura se emitirá en breve.</p>
            </div>
          )}
          {mpPaymentStatus === 'pending' && (
            <div className="mb-4 rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800 dark:border-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200">
              <p className="font-semibold">Pago en proceso</p>
              <p className="mt-1">Tu pago está siendo procesado. Si pagaste con Rapipago o PagoFácil, puede demorar hasta 48 hs en acreditarse.</p>
            </div>
          )}
          {mpPaymentStatus === 'failure' && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200">
              <p className="font-semibold">Pago no realizado</p>
              <p className="mt-1">El pago fue rechazado o cancelado. Podés intentarlo nuevamente.</p>
            </div>
          )}

          {ultimoPagoRechazado && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
              <div className="flex items-start gap-2">
                <span className="material-symbols-outlined">cancel</span>
                <div>
                  <p className="font-semibold">Pago rechazado</p>
                  {ultimoPagoRechazado.observaciones && (
                    <p className="mt-1">{ultimoPagoRechazado.observaciones}</p>
                  )}
                  <p className="mt-1 text-xs text-red-700">
                    Fecha: {formatearFechaHora(ultimoPagoRechazado.fechaPago)}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Breadcrumb */}
          <div className="flex items-center gap-1.5 mb-5">
            <button
              onClick={() => navigate('/cliente/dashboard')}
              className="text-[#617c89] dark:text-gray-400 text-sm hover:text-[#007BFF] transition-colors"
            >
              Mis Boletas
            </button>
            <svg className="w-3.5 h-3.5 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-sm font-medium text-[#111618] dark:text-white">
              Boleta #{boleta.id} — {formatearPeriodo(boleta.mes, boleta.anio)}
            </span>
          </div>

          {esPlanPago && (
            <div className="mb-5 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-800 dark:border-indigo-900/30 dark:bg-indigo-900/20 dark:text-indigo-100">
              Esta boleta está adherida a un plan de pago. Las cuotas se cobrarán en las próximas boletas.
            </div>
          )}

          {/* Grid Principal */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Columna Izquierda: Detalles */}
            <div className="lg:col-span-3 flex flex-col gap-5">

              {/* Card unificada: Socio + Resumen */}
              <div className="bg-white dark:bg-[#1A2A33] rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                {/* Header de la card */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700">
                  <div>
                    <p className="text-xs text-[#617c89] dark:text-gray-400 uppercase tracking-wide font-medium">Boleta de agua</p>
                    <h1 className="text-lg font-bold text-[#111618] dark:text-white mt-0.5">
                      {formatearPeriodo(boleta.mes, boleta.anio)}
                    </h1>
                  </div>
                  {boleta.estado === 'pagada' ? (
                    <span className="inline-flex items-center rounded-full bg-green-500/15 px-3 py-1 text-xs font-semibold text-green-700 dark:text-green-400">
                      Pagada
                    </span>
                  ) : boleta.estado === 'procesando' ? (
                    <span className="inline-flex items-center rounded-full bg-blue-500/15 px-3 py-1 text-xs font-semibold text-blue-700 dark:text-blue-400">
                      En verificación
                    </span>
                  ) : boleta.estado === 'plan_pago' ? (
                    <span className="inline-flex items-center rounded-full bg-indigo-500/15 px-3 py-1 text-xs font-semibold text-indigo-700 dark:text-indigo-400">
                      Plan de pago
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-amber-500/15 px-3 py-1 text-xs font-semibold text-amber-700 dark:text-amber-400">
                      Pendiente
                    </span>
                  )}
                </div>

                {/* Monto + fechas */}
                <div className="px-6 py-5 flex flex-wrap items-center gap-6 border-b border-gray-100 dark:border-gray-700">
                  <div>
                    <p className="text-xs text-[#617c89] dark:text-gray-400 mb-1">Total a pagar</p>
                    <p className="text-3xl font-bold text-[#007BFF] tabular-nums">${formatearMonto(boleta.total)}</p>
                  </div>
                  <div className="flex gap-6 text-sm">
                    <div>
                      <p className="text-xs text-[#617c89] dark:text-gray-400 mb-1">Emisión</p>
                      <p className="font-medium text-[#111618] dark:text-gray-200">{formatearFecha(boleta.fechaEmision)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#617c89] dark:text-gray-400 mb-1">Vencimiento</p>
                      <p className="font-medium text-[#111618] dark:text-gray-200">{formatearFecha(boleta.fechaVencimiento)}</p>
                    </div>
                  </div>
                </div>

                {/* Datos del socio */}
                <div className="px-6 py-4 flex flex-wrap gap-x-8 gap-y-2 text-sm">
                  <div>
                    <span className="text-[#617c89] dark:text-gray-400">Titular: </span>
                    <span className="font-medium text-[#111618] dark:text-gray-200">{user.nombre}</span>
                  </div>
                  <div>
                    <span className="text-[#617c89] dark:text-gray-400">Padrón: </span>
                    <span className="font-medium text-[#111618] dark:text-gray-200">{user.padron}</span>
                  </div>
                  {user.direccion && (
                    <div>
                      <span className="text-[#617c89] dark:text-gray-400">Dirección: </span>
                      <span className="font-medium text-[#111618] dark:text-gray-200">{user.direccion}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Detalle de Cargos */}
              <div className="bg-white dark:bg-[#1A2A33] rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
                  <h2 className="text-base font-semibold text-[#111618] dark:text-white">Detalle de cargos</h2>
                </div>
                <div className="p-6 space-y-3">
                  {/* Servicio Base */}
                  <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-700">
                    <span className="text-gray-600 dark:text-gray-300">Servicio de Agua Potable</span>
                    <span className="font-semibold dark:text-gray-200">
                      ${formatearMonto(boleta.monto_servicio_base)}
                    </span>
                  </div>

                  {/* Consumo Escalonado */}
                  {boleta.tiene_medidor && parseFloat(boleta.consumo_m3) > 0 && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 space-y-2">
                      <p className="text-sm font-semibold text-[#007BFF] mb-2">Consumo por Escalas</p>
                      
                      {boleta.desglose_consumo && boleta.desglose_consumo.length > 0 ? (
                        <>
                          {boleta.desglose_consumo.map((escala, index) => (
                            <div key={index} className="flex justify-between text-sm">
                              <span className="text-gray-600 dark:text-gray-400">
                                Consumo {escala.desde}-{escala.hasta || '∞'} m³: {parseFloat(escala.consumo_m3).toFixed(2)} m³
                                {escala.precio_por_m3 > 0 && ` × $${formatearMonto(escala.precio_por_m3)}`}
                              </span>
                              <span className="text-gray-600 dark:text-gray-300">
                                {escala.precio_por_m3 === 0 ? 'Incluido en servicio base' : `$${formatearMonto(escala.subtotal)}`}
                              </span>
                            </div>
                          ))}
                        </>
                      ) : (
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          Sin desglose disponible
                        </div>
                      )}

                      <div className="flex justify-between pt-2 border-t border-blue-200 dark:border-blue-800">
                        <span className="font-semibold text-[#111618] dark:text-white">Total Consumo</span>
                        <span className="font-semibold text-[#111618] dark:text-white">
                          ${formatearMonto(boleta.monto_consumo)}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Otros Cargos */}
                  {parseFloat(boleta.total_cargos_extras) > 0 && (
                    <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-700">
                      <span className="text-gray-600 dark:text-gray-300">Otros Cargos</span>
                      <span className="font-semibold dark:text-gray-200">
                        ${formatearMonto(boleta.total_cargos_extras)}
                      </span>
                    </div>
                  )}

                  {/* Total */}
                  <div className="flex justify-between items-center py-3 pt-4 border-t-2 border-gray-300 dark:border-gray-600">
                    <span className="text-lg font-bold text-[#111618] dark:text-white">Total</span>
                    <span className="text-lg font-bold text-[#007BFF]">
                      ${formatearMonto(boleta.total)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Historial de Consumo */}
              {lecturas.length > 0 && (
                <div className="bg-white dark:bg-[#1A2A33] rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
                    <h2 className="text-base font-semibold text-[#111618] dark:text-white">Historial de consumo <span className="text-xs font-normal text-[#617c89] dark:text-gray-400 ml-1">últimos {lecturas.length} meses</span></h2>
                  </div>
                  <div className="p-6">
                    <div className="flex justify-around items-end h-32 gap-3">
                      {lecturas.map((lectura, index) => {
                        const altura = calcularAltura(lectura.consumoM3);
                        const esActual = index === lecturas.length - 1;
                        return (
                          <div key={index} className="flex flex-col items-center gap-1 w-full text-center">
                            <div 
                              className={`${
                                esActual ? 'bg-[#007BFF]' : 'bg-[#007BFF]/20'
                              } rounded-t-md w-full transition-all`}
                              style={{ 
                                height: altura > 0 ? `${altura}%` : '10%',
                                minHeight: '15px'
                              }}
                            />
                            <span className={`text-xs ${
                              esActual 
                                ? 'text-[#007BFF] font-bold' 
                                : 'text-gray-500 dark:text-gray-400'
                            }`}>
                              {['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'][lectura.mes - 1]}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Columna Derecha: Panel de Pago */}
            <div className="lg:col-span-2">
              <div className="bg-white dark:bg-[#1A2A33] rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden sticky top-5">
                {/* Header del panel con total */}
                <div className="px-6 py-5 border-b border-gray-100 dark:border-gray-700">
                  <p className="text-xs text-[#617c89] dark:text-gray-400 uppercase tracking-wide font-medium mb-1">Total a pagar</p>
                  <p className="text-3xl font-bold text-[#007BFF] tabular-nums">${formatearMonto(boleta.total)}</p>
                  <p className="text-xs text-[#617c89] dark:text-gray-400 mt-1">Vence el {formatearFecha(boleta.fechaVencimiento)}</p>
                </div>

                <div className="p-6">
                {boleta.estado === 'pagada' ? (
                  <div className="flex flex-col items-center gap-2 py-4 text-center">
                    <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center">
                      <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-sm font-semibold text-green-700 dark:text-green-400">Boleta abonada</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Esta boleta ya fue pagada correctamente.</p>
                  </div>
                ) : esPlanPago ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Esta boleta está en plan de pago. No es posible pagar individualmente.
                  </p>
                ) : (
                  <div className="flex flex-col gap-5">
                    {sinMetodosPermitidos && (
                      <div className="rounded-xl border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800 dark:border-yellow-900/40 dark:bg-yellow-900/20 dark:text-yellow-100">
                        No hay métodos de pago disponibles en este momento. Comunicate con administración.
                      </div>
                    )}

                    {/* Selector de método */}
                    <div className="flex flex-col gap-2">
                      {/* MP Online */}
                      <button
                        onClick={() => mercadoPagoPermitido && setMetodoPago('mercadopago')}
                        disabled={!mercadoPagoPermitido}
                        className={`flex items-center gap-3 p-3.5 rounded-xl border-2 transition-all text-left w-full ${
                          !mercadoPagoPermitido
                            ? 'border-gray-200 dark:border-gray-700 opacity-50 cursor-not-allowed'
                            : metodoPago === 'mercadopago'
                              ? 'border-[#009EE3] bg-[#009EE3]/8 shadow-sm'
                              : 'border-gray-200 dark:border-gray-700 hover:border-[#009EE3]/40 hover:bg-gray-50 dark:hover:bg-white/5'
                        }`}
                      >
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
                          !mercadoPagoPermitido
                            ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600'
                            : metodoPago === 'mercadopago'
                              ? 'bg-[#009EE3] text-white'
                              : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
                        }`}>
                          <span className="text-xs font-black tracking-tight">MP</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-semibold leading-tight ${
                            !mercadoPagoPermitido
                              ? 'text-gray-400 dark:text-gray-600'
                              : metodoPago === 'mercadopago'
                                ? 'text-[#009EE3]'
                                : 'text-gray-800 dark:text-gray-200'
                          }`}>
                            Mercado Pago
                          </p>
                          <p className="text-xs text-gray-400 dark:text-gray-500 leading-tight mt-0.5">
                            Tarjeta, débito o cuenta MP
                          </p>
                        </div>
                        {!mpHabilitado ? (
                          <span className="text-xs font-semibold text-gray-400 dark:text-gray-600 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-full flex-shrink-0">
                            Próximamente
                          </span>
                        ) : !metodosPermitidos.mercadoPago ? (
                          <span className="text-xs font-semibold text-gray-400 dark:text-gray-600 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-full flex-shrink-0">
                            No habilitado
                          </span>
                        ) : metodoPago === 'mercadopago' && (
                          <svg className="w-4 h-4 text-[#009EE3] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>

                      {/* Transferencia */}
                      <button
                        onClick={() => transferenciaPermitida && setMetodoPago('transferencia')}
                        disabled={!transferenciaPermitida}
                        className={`flex items-center gap-3 p-3.5 rounded-xl border-2 transition-all text-left w-full ${
                          !transferenciaPermitida
                            ? 'border-gray-200 dark:border-gray-700 opacity-50 cursor-not-allowed'
                            : metodoPago === 'transferencia'
                            ? 'border-[#007BFF] bg-[#007BFF]/8 shadow-sm'
                            : 'border-gray-200 dark:border-gray-700 hover:border-[#007BFF]/40 hover:bg-gray-50 dark:hover:bg-white/5'
                        }`}
                      >
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
                          transferenciaPermitida && metodoPago === 'transferencia' ? 'bg-[#007BFF] text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
                        }`}>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-semibold leading-tight ${
                            !transferenciaPermitida
                              ? 'text-gray-400 dark:text-gray-600'
                              : metodoPago === 'transferencia'
                                ? 'text-[#007BFF]'
                                : 'text-gray-800 dark:text-gray-200'
                          }`}>
                            Transferencia bancaria
                          </p>
                          <p className="text-xs text-gray-400 dark:text-gray-500 leading-tight mt-0.5">
                            Subí el comprobante para verificación
                          </p>
                        </div>
                        {!transferenciaPermitida ? (
                          <span className="text-xs font-semibold text-gray-400 dark:text-gray-600 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-full flex-shrink-0">
                            No habilitado
                          </span>
                        ) : metodoPago === 'transferencia' && (
                          <svg className="w-4 h-4 text-[#007BFF] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>
                    </div>

                    {/* Panel Mercado Pago */}
                    {metodoPago === 'mercadopago' && (
                      <div className="flex flex-col gap-3">
                        <div className="rounded-xl bg-[#009EE3]/8 border border-[#009EE3]/25 p-4 text-sm">
                          <p className="font-semibold text-[#009EE3] mb-2">Serás redirigido a Mercado Pago</p>
                          <ul className="space-y-1 text-gray-600 dark:text-gray-300">
                            {['Tarjeta de crédito o débito', 'Cuenta de Mercado Pago', 'Transferencia bancaria'].map((item) => (
                              <li key={item} className="flex items-center gap-2 text-xs">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#009EE3] flex-shrink-0" />
                                {item}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <button
                          onClick={handlePagarConMP}
                          disabled={mpLoading || !mercadoPagoPermitido}
                          className="w-full flex items-center justify-center h-12 bg-[#009EE3] text-white text-sm font-bold rounded-xl hover:bg-[#009EE3]/90 disabled:bg-gray-300 disabled:cursor-not-allowed dark:disabled:bg-gray-600 transition-colors gap-2 shadow-sm"
                        >
                          {mpLoading ? (
                            'Redirigiendo...'
                          ) : (
                            <>
                              <span className="font-black text-base">MP</span>
                              Continuar con Mercado Pago
                            </>
                          )}
                        </button>
                      </div>
                    )}

                    {/* Panel Transferencia */}
                    {metodoPago === 'transferencia' && (
                      <>
                        {/* Instrucciones */}
                        <div>
                          <h3 className="font-semibold text-base mb-2 dark:text-gray-200">
                            Instrucciones de Pago
                          </h3>
                          <ol className="list-decimal list-inside text-sm text-gray-600 dark:text-gray-300 space-y-1">
                            <li>Copia el alias para realizar la transferencia.</li>
                            <li>Realiza el pago desde tu home banking.</li>
                            <li>Sube el comprobante de pago en esta pantalla.</li>
                            <li>Haz clic en "Confirmar Pago" para finalizar.</li>
                          </ol>
                        </div>

                        {/* Alias */}
                        <div>
                          <h3 className="font-semibold text-base mb-2 dark:text-gray-200">
                            Datos para la Transferencia
                          </h3>
                          <div className="flex items-center justify-between gap-2 p-3 border rounded-lg bg-gray-50 dark:bg-[#101c22] dark:border-gray-600">
                            <p className="font-mono text-sm text-gray-700 dark:text-gray-300">
                              LIMA.RACIMO.PASADO
                            </p>
                            <button
                              id="copy-btn"
                              onClick={handleCopiarAlias}
                              className="flex items-center justify-center p-2 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-gray-600 dark:text-gray-400"
                              title="Copiar alias"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </button>
                          </div>
                        </div>

                        {/* Upload de Comprobante */}
                        <div>
                          <h3 className="font-semibold text-base mb-2 dark:text-gray-200">
                            Adjuntar Comprobante
                          </h3>
                          <div className="flex items-center justify-center w-full">
                            <label
                              htmlFor="dropzone-file"
                              className="flex flex-col items-center justify-center w-full h-40 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:border-gray-600 dark:hover:border-gray-500 dark:hover:bg-gray-700"
                            >
                              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                {comprobante ? (
                                  <>
                                    <svg className="w-10 h-10 mb-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    <p className="text-sm text-gray-600 dark:text-gray-300 font-semibold">
                                      {comprobante.name}
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                      Click para cambiar
                                    </p>
                                  </>
                                ) : (
                                  <>
                                    <svg className="w-10 h-10 mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                    </svg>
                                    <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                                      <span className="font-semibold">Haz clic para subir</span> o arrastra y suelta
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                      PDF, PNG, JPG (MAX. 5MB)
                                    </p>
                                  </>
                                )}
                              </div>
                              <input
                                id="dropzone-file"
                                type="file"
                                className="hidden"
                                accept=".pdf,.png,.jpg,.jpeg"
                                onChange={handleFileChange}
                              />
                            </label>
                          </div>
                        </div>

                        {/* Botón Confirmar */}
                        <button
                          onClick={handleConfirmarPago}
                          disabled={!comprobante || uploading || !transferenciaPermitida}
                          className="w-full flex items-center justify-center h-12 bg-[#007BFF] text-white text-base font-bold rounded-lg hover:bg-[#007BFF]/90 disabled:bg-gray-300 disabled:cursor-not-allowed dark:disabled:bg-gray-600 transition-colors"
                        >
                          {uploading ? 'Procesando...' : 'Confirmar Pago'}
                        </button>
                      </>
                    )}
                  </div>
                )}
                </div>
              </div>
            </div>
          </div>
      </main>

      <Modal
        isOpen={modal.isOpen}
        onClose={cerrarModal}
        type={modal.type}
        title={modal.title}
        message={modal.message}
        onConfirm={cerrarModal}
      />
    </div>
  );
}

export default PagarBoleta;
